/**
 * THE OFFICE'S OWN WORDS TO A BOOKED CLIENT, BY TEXT OR BY EMAIL, AND ITS REPLY TO HER TEXT (ops #32;
 * the email since ops #84 phase 5). One component, used on a household's thread (`TextThread.tsx`),
 * under a text on the Texts list (`Messages.tsx`) and on the household's Emails card
 * (`HouseholdEmails.tsx`), and one door behind it, `POST /api/messages/compose`, which asks every
 * question below again and refuses in words: everything here is UX, never the control (binding rule
 * 6). Ops composes; the phone system delivers a text and home delivers an email from home@; nothing
 * here sends anything (the third rule at the seams), and no word in the box is written by anything but
 * a person (D13): the person typing it, or the business's own approved words of message 11, filled
 * from the record and put in on one press (below); there is nothing on either channel that writes the
 * words for them (`api/test/no-ai.test.ts`).
 *
 * THE CHANNEL. `Composer` is opened with the channels its place offers, and where it offers two a
 * switch above the box moves between them, each with its own people (a number for a text, an address
 * for an email) and its own read before anybody types. `TextComposer` is the text alone, as the thread
 * and the Texts list have always opened it; a reply to her text is a text, always.
 *
 * AN EMAIL is the office's words under the fixed subject of its kind (`officeEmailSubject`, E8),
 * shown above the box and never typed, the clean's day in it where the kind names one, and the footer
 * after the words, shown beneath them. It asks no signature and no opt-out line. Send is disabled with
 * the reason where they have no address, the register refuses them, or the switch for client email
 * refuses it: while client email is switched off the composer says plainly that the email cannot be
 * sent and that nothing is saved, since the door writes nothing. A
 * link in the words goes only to a page at home; the door refuses any other, and says so.
 *
 * WHO. A reply goes to a booked client whose website box grant stands, and to nobody else (the owner,
 * 2026-10-16). Answering her text, the person is hers; writing a new text on a household, a picker
 * offers her people with a number. As it opens it reads `GET /api/messages/compose/:contactId`: the
 * register, the go-live switch, the hours, the three a day and the confirmation ahead.
 *
 * THE BOX starts with the signature the campaign filed, "Faithfully Kept, this is <first name>: ", and
 * under it, live as she types: the parts the phone system will count, after the opt-out line the door
 * adds where the words do not carry it; in coral, each claim and each price the server would refuse,
 * from the same list it refuses with (`checkWords` in `@fk/shared`), with what to change, and Send
 * disabled while one stands; in amber, what only reads like a door code, a price or a cut, which
 * leaves Send enabled because the server cannot tell those from ordinary office English and does not
 * refuse them; and the hours, the count and the confirmation ahead.
 *
 * SEND IS DISABLED WITH THE REASON beside it where the register or the switch refuses her, where the
 * phone system holds her number (ops #33), while a finding stands, past three parts, and until
 * something is written after the signature. A stop in her words waiting for a decision, and a held
 * number, are said as the hold's card and the text that failed say them, with where each is decided:
 * `onTextsList` tells the Texts list, where the band of stops is above and the failed text is not,
 * from her household's page. A refusal of the door is said in words and never its code, what was
 * typed stays in the box, and a refusal by the register, the switch or the held number reads her
 * again, so the reason beside Send is the one that stands now.
 *
 * MESSAGE 11, A FINISHED CLEAN, WAITS FOR WHAT THE CLIENT READS (ops #98 phase 4,
 * `docs/plan-client-language.md` section 6). On either channel, kind `visit_finished` asks for the visit
 * it is about, then reads `GET /api/messages/compose/:contactId/finished/:visitId` and shows "What the
 * client reads of the report" beside the box: the household's language and the office's words for
 * the client, never the cleaner's, with the stale-language note where they were written for another.
 * Send waits until those words exist, as the door does. On an email for an English household whose
 * words are English, one press, "Use the approved words", puts message 11's approved sentences in the
 * box, filled by the server from the business's copy with the home, the cleaner's first name and the
 * office's words as the journal shows them; they are the office's to change and go as its own words,
 * under the same check. Nothing is filled for any other language, since the business has approved the
 * fixed words in English alone (ops #98): the office types a Spanish household's note, quoting what
 * the client reads, and the subject and the footer stay English. A text of 11 carries no note and is
 * filled nothing. The press copies the server's words and nothing else: no word in the box is made
 * here. Where a thing they check first is photographed, the server's words carry message 34 inside 11
 * (ops #86 phase 4, the business's `.10`), and the panel says which things, and whether the words carry
 * their page at home or, with no door open there, no link at all.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  CODE_WARNING,
  COMPOSER_KINDS,
  EMAIL_OFF,
  FOOTER,
  MAX_TEXT_PARTS,
  NOT_A_TEST_ADDRESS,
  OPT_OUT_LINE,
  VISIT_STATUS_LABELS,
  checkWords,
  looksLikeACode,
  officeEmailSubject,
  textParts,
  warningsIn,
  type ClaimsFinding,
  type ComposerKind,
} from '@fk/shared';
import {
  ApiError,
  composeText,
  getComposeContext,
  getEmailComposeContext,
  getFinishedWords,
  type ComposeContext,
  type EmailComposeContext,
  type ComposedText,
  type FinishedWords,
  type MessageVisit,
} from '../api.ts';
import { MEASURE } from '../components/AppShell.tsx';
import { Button, SelectField, TARGET_SIZE, TextAreaField } from '../components/Field.tsx';
import { Busy, ErrorNotice } from '../components/Notice.tsx';
import { choiceLineOf } from '../choices.ts';
import { shortDay, shortInstant } from '../format.ts';
import { holdBarsTextsWords } from '../holds.ts';
import { CLIENT_LANGUAGE_LABELS, COMPOSER_KIND_LABELS } from '../labels.ts';

/**
 * Somebody a message may be written to, with what reaches them where the place knows it: a number for
 * a text, an address for an email. A field left out is not known here, and the door decides.
 */
export interface ComposerPerson {
  readonly id: string;
  readonly displayName: string;
  readonly phoneE164?: string | null;
  readonly email?: string | null;
}

/** The text of hers a reply answers: its ops id, whose it is, and her words, quoted above the box. */
export interface AnsweredText {
  readonly id: string;
  readonly contactId: string;
  readonly body: string | null;
}

/** D9's hours, which the phone system holds a text to, as the screen says them. */
const TEXTING_HOURS_WORDS = '8:00 am to 8:00 pm';

/** How the phone system reads a body for its opt-out line, and so how the door does. */
const OPT_OUT_WORDS = 'stop to opt out';

/** The register's answer where it gives no sentence of its own. */
const NOT_TICKED =
  'They have not ticked the texting box on the website, so nothing can be texted to them.';

/**
 * A SENTENCE OF THE SERVER'S IN PLAIN WORDS. The register and the go-live switch write theirs for the
 * row a refused text is parked with, led by a word for tools, `refused:` or `not_enabled:`; a person
 * reads the sentence after it, begun with a capital and ended with a full stop.
 */
export function plainWords(sentence: string): string {
  const words = sentence.replace(/^[a-z_]+:\s*/, '').trim();
  if (words === '') return sentence;
  const capital = `${words.charAt(0).toUpperCase()}${words.slice(1)}`;
  return /[.!?]$/.test(capital) ? capital : `${capital}.`;
}

/**
 * Why the register will not have her texted, and the way on. A held stop is said in the hold's own
 * words and where it is decided (`holdBarsTextsWords`), never in the server's sentence, which carries
 * the hold's id and its day as a date in code; somebody marked do not contact is offered no way on,
 * because a mark may bar calls and email too and her read carries only the one on texts (ops #44).
 */
function registerWords(texting: ComposeContext['texting'], onTextsList: boolean): string {
  if (texting.holdId !== null) return holdBarsTextsWords(onTextsList);
  const why = texting.refusal === null ? NOT_TICKED : plainWords(texting.refusal);
  if (texting.markId !== null) return why;
  return `${why} Ring them, or email them from the office mailbox.`;
}

/**
 * WHY NOTHING CAN GO WHILE THE PHONE SYSTEM HOLDS HER NUMBER (ops #33): a text written now would fail
 * as the one before it did, and so would the confirmation ahead of it. The way on is the text that
 * failed, which says to call her and offers Number confirmed until somebody presses it, and then where
 * the release stands; on the Texts list, that text is on her household's page.
 */
function numberHeldWords(held: 'held' | 'asked', onTextsList: boolean): string {
  return held === 'asked'
    ? `The phone system holds their number since a text of ours to it failed, and it has been asked to release it. Write to them once it has: the text that failed says when${onTextsList ? ", on their household's page" : ''}.`
    : `The phone system holds their number since a text of ours to it failed, so this one would fail too. Call them to confirm the number, then press Number confirmed under that text${onTextsList ? " on their household's page" : ''}.`;
}

/** Why the go-live switch holds texts to her number, and who moves it. */
function switchWords(refusal: string): string {
  const why = plainWords(refusal);
  return why.includes('Settings') ? why : `${why} An owner sets the texting switch in Settings.`;
}

/** What the door wrote, as the office reads it once the box has closed. */
export function sentWords(result: ComposedText): string {
  return [
    'Approved. The phone system sends it.',
    ...(result.handled ? ['Their text is marked handled.'] : []),
    ...(result.confirmationAhead ? ['The opt-in confirmation goes ahead of it.'] : []),
  ].join(' ');
}

/** A refusal of the door: its code, which picks the way on, its sentence, and what the check found. */
interface Refused {
  readonly code: string;
  readonly message: string;
  readonly findings: readonly ClaimsFinding[];
}

/** The findings a claims refusal carries in its body, and nothing that is not one. */
function findingsOf(body: unknown): ClaimsFinding[] {
  const list = (body as { findings?: unknown } | null)?.findings;
  if (!Array.isArray(list)) return [];
  return list.filter(
    (one): one is ClaimsFinding =>
      typeof one === 'object' &&
      one !== null &&
      typeof (one as ClaimsFinding).phrase === 'string' &&
      typeof (one as ClaimsFinding).say === 'string',
  );
}

/**
 * A refusal in the office's words: the server's sentence, and the way on where there is one. The
 * register's way on is offered only once her read again agrees the register refuses her, so a read
 * from before the refusal never vouches for ringing somebody marked since; and a number the phone
 * system holds is said as the screen says it once her read again agrees, with where its text is.
 */
function refusedWords(
  refused: Refused,
  context: ComposeContext | null,
  onTextsList: boolean,
): string {
  if (refused.code === 'texts_not_allowed') {
    return context !== null && !context.texting.allowed
      ? registerWords({ ...context.texting, refusal: refused.message }, onTextsList)
      : plainWords(refused.message);
  }
  if (refused.code === 'texting_off') return switchWords(refused.message);
  if (refused.code === NUMBER_HELD && context?.numberHeld) {
    return numberHeldWords(context.numberHeld, onTextsList);
  }
  return refused.message;
}

/** The door's word for a number the phone system holds (ops #33). */
const NUMBER_HELD = 'number_held';

/** The refusals after which her read is asked again, so the reason beside Send is the one now. */
const READ_AGAIN_ON: ReadonlySet<string> = new Set([
  'texts_not_allowed',
  'texting_off',
  NUMBER_HELD,
]);

/** How many parts the words go as, and the line the door adds; past three, that it is too long. */
function partsWords(parts: number, optOutAdded: boolean): string {
  if (parts > MAX_TEXT_PARTS) {
    return `${parts} parts is too long: the phone system takes three at most. Shorten it, or email them from the office mailbox.`;
  }
  return `${parts} of ${MAX_TEXT_PARTS} parts${optOutAdded ? `, with "${OPT_OUT_LINE}" added at the end` : ''}.`;
}

/** Why Send is disabled, before anything is sent, or null when it may be pressed. */
function whyNot(
  context: ComposeContext,
  findings: number,
  parts: number,
  words: string,
  onTextsList: boolean,
  kind: ComposerKind = 'reply',
  finished: string | null = null,
): string | null {
  if (!context.texting.allowed) return registerWords(context.texting, onTextsList);
  if (context.switch.refusal !== null) return switchWords(context.switch.refusal);
  // Her choice or the office's hold on this kind (ops #76 phase 2), after the register and the switch
  // as the door asks them, so the office reads it before it presses.
  const chosen = choiceLineOf(context.choices?.[kind]);
  if (chosen !== null) return chosen;
  if (context.numberHeld) return numberHeldWords(context.numberHeld, onTextsList);
  // Message 11 waits for what the client reads of its visit's report (ops #98 phase 4).
  if (finished !== null) return finished;
  if (findings > 0) return 'Change the words marked in coral, then send.';
  if (parts > MAX_TEXT_PARTS) return 'Shorten it to three parts, then send.';
  if (words === '' || words === context.signature.trim()) {
    return 'Write what they should read after the signature.';
  }
  return null;
}

/* ------------------------------------------------------------------ *
 * Message 11, a finished clean (ops #98 phase 4).
 * ------------------------------------------------------------------ */

/** A finished clean composed with no visit chosen. */
const CHOOSE_THE_VISIT =
  'Choose the visit it is about: a message that the clean is finished waits for what the client reads of its report.';

/** A finished clean on a household with no visit the composer offers. */
const NO_VISIT_TO_CHOOSE =
  'A message that the clean is finished is about one visit, and this household has none a week back or two ahead to choose.';

/** A finished clean whose report has no words for the client yet: the door refuses it too. */
const NOT_FOR_CLIENT_YET =
  "Read the cleaner's report on the visit's job sheet and write what the client reads first. Nothing of this message goes before.";

/** What the client reads of the chosen visit's report, as the composer has it. */
type FinishedRead =
  | { readonly state: 'none' }
  | { readonly state: 'reading' }
  | { readonly state: 'read'; readonly words: FinishedWords }
  | { readonly state: 'failed'; readonly error: ApiError };

/**
 * THE READ FOR MESSAGE 11, once kind `visit_finished` and a visit are chosen, again whenever either
 * changes, and nothing for any other kind: what the client reads of that visit's report.
 */
function useFinishedWords(
  contactId: string,
  kind: ComposerKind,
  visitId: string,
): readonly [FinishedRead, () => void] {
  const [read, setRead] = useState<FinishedRead>({ state: 'none' });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (kind !== 'visit_finished' || visitId === '' || contactId === '') {
      setRead({ state: 'none' });
      return;
    }
    let live = true;
    setRead({ state: 'reading' });
    getFinishedWords(contactId, visitId)
      .then((words) => {
        if (live) setRead({ state: 'read', words });
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        setRead({
          state: 'failed',
          error:
            thrown instanceof ApiError
              ? thrown
              : new ApiError(0, 'error', 'What the client reads of that report did not load.'),
        });
      });
    return () => {
      live = false;
    };
  }, [contactId, kind, visitId, attempt]);
  return [read, () => setAttempt((n) => n + 1)];
}

/** Why a finished clean cannot go yet, or null where it may, or where the kind is another. */
function finishedWhyNot(
  kind: ComposerKind,
  visitId: string,
  visits: readonly MessageVisit[],
  read: FinishedRead,
): string | null {
  if (kind !== 'visit_finished') return null;
  if (visitId === '') return visits.length === 0 ? NO_VISIT_TO_CHOOSE : CHOOSE_THE_VISIT;
  if (read.state === 'read') return read.words.forClient === null ? NOT_FOR_CLIENT_YET : null;
  if (read.state === 'failed') {
    return 'What the client reads of that report did not load. Try it again above.';
  }
  return 'Reading what the client reads of that report.';
}

/** The things a photo report names, as the server's words name them: "A", "A and B", "A, B and C". */
function listedLines(lines: readonly string[]): string {
  return lines.length < 2
    ? (lines[0] ?? '')
    : `${lines.slice(0, -1).join(', ')} and ${lines[lines.length - 1]}`;
}

/**
 * "WHAT THE CLIENT READS OF THE REPORT", beside the box of a finished clean: the household's language,
 * the office's words for the client or that there are none yet with the way to the job sheet, the
 * stale-language note, and on an email the one press that puts the approved words in the box, or why
 * nothing is filled. Never the cleaner's words, which the read does not carry.
 */
function WhatTheClientReads({
  read,
  channel,
  onRetry,
  onUse,
}: {
  read: FinishedRead;
  channel: ComposerChannel;
  onRetry: () => void;
  /** The approved words, put in the box in place of what it holds; an email's alone. */
  onUse?: (words: string) => void;
}) {
  if (read.state === 'none') return null;
  if (read.state === 'reading') return <Busy what="what the client reads of the report" />;
  if (read.state === 'failed') {
    return (
      <div className="mb-3">
        <ErrorNotice
          title="What the client reads did not load"
          message={read.error.message}
          onRetry={onRetry}
        />
      </div>
    );
  }
  const { words } = read;
  const language = CLIENT_LANGUAGE_LABELS[words.householdLanguage];
  const forClient = words.forClient;
  const stale =
    forClient !== null &&
    forClient.language !== null &&
    forClient.language !== words.householdLanguage
      ? CLIENT_LANGUAGE_LABELS[forClient.language]
      : null;
  const approved = words.approved;
  return (
    <section
      data-finished
      aria-label="What the client reads of the report"
      className={`mb-3 rounded-lg border border-navy/15 bg-cream px-3 py-2 text-sm ${MEASURE}`}
    >
      <h5 className="font-medium">What the client reads of the report</h5>
      <p className="mt-1 text-navy/80">{`This household reads ${language}.`}</p>
      {forClient === null ? (
        <p className="mt-1 wrap-anywhere">
          {'No words for the client yet. '}
          <Link
            to={`/visits/${encodeURIComponent(words.visitId)}/sheet`}
            className="font-medium underline"
          >
            Open the visit's job sheet
          </Link>
          {" to read the cleaner's report and write them."}
        </p>
      ) : (
        <>
          <blockquote
            data-for-client-words
            className="mt-2 border-l-4 border-navy/20 pl-3 whitespace-pre-wrap wrap-anywhere"
          >
            {forClient.whatWasDone}
          </blockquote>
          {stale !== null ? (
            <p className="mt-1">
              {`Written in ${stale}; this household reads ${language} now. Write them again on the job sheet.`}
            </p>
          ) : null}
          {channel === 'sms' ? (
            <p className="mt-1 text-navy/70">
              A text of this message carries no note: the email quotes what the client reads.
            </p>
          ) : approved !== null ? (
            <div className="mt-2">
              <Button tone="quiet" onClick={() => onUse?.(approved.email)}>
                Use the approved words
              </Button>
              <p className="mt-1 text-navy/70">
                {`The business's approved words of this message (${approved.version}), with what the client reads quoted, put in the box in place of what it holds. They go as your own words, to change before you send.`}
              </p>
              {approved.photoReport !== null ? (
                <p data-photo-report className="mt-1 text-navy/70 wrap-anywhere">
                  {`With the photo report of what they check first: ${listedLines(approved.photoReport.lines)}. ${
                    approved.photoReport.linked
                      ? 'The words carry their page at home: the faces of "How did we do?" and "See the report and photos".'
                      : 'No link to the report and photos and no faces: no page of theirs at home opens for them yet.'
                  }`}
                </p>
              ) : null}
            </div>
          ) : words.householdLanguage !== 'en' ? (
            <p className="mt-1">
              {`The approved words of this message are English, and the business has approved no ${language} form of them yet, so nothing is filled in: write it in ${language} in your own words, quoting what the client reads above. Its subject and its footer stay English until the business approves ${language} ones.`}
            </p>
          ) : stale === null && !words.cleanerNamed ? (
            <p className="mt-1">
              The visit names no cleaner now, so the approved words, which name them, are not filled
              in.
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}

const visitWords = (visits: readonly MessageVisit[]) => (id: string) => {
  const visit = visits.find((one) => one.id === id);
  return visit ? `${shortDay(visit.scheduledDate)}, ${VISIT_STATUS_LABELS[visit.status]}` : id;
};

/** A line under the box that matters before Send: drawn with the coral edge. */
const CORAL_LINE =
  'rounded-lg border border-coral/50 border-l-4 border-l-coral bg-parchment px-3 py-2';

/** A line that only asks her to read again: drawn with the gold edge. */
const AMBER_LINE =
  'rounded-lg border border-gold/60 border-l-4 border-l-gold bg-parchment px-3 py-2';

/** The two ways the office's words go: a text the phone system delivers, an email home delivers. */
export type ComposerChannel = 'sms' | 'email';

/** The switch's words for each channel. */
const CHANNEL_LABELS: Readonly<Record<ComposerChannel, string>> = {
  sms: 'Text',
  email: 'Email',
};

/** Whether a person can be reached on a channel: a number for a text, an address for an email. */
function reachableBy(channel: ComposerChannel) {
  return (person: ComposerPerson) =>
    channel === 'sms' ? person.phoneE164 !== null : person.email !== null;
}

/**
 * THE OFFICE'S WORDS, ON THE CHANNELS ITS PLACE OFFERS. A reply to her text is always a text. Where two
 * channels are offered the switch moves between them, each with the people it reaches.
 */
export function Composer({
  people,
  channels = ['sms'],
  answering = null,
  quote = true,
  onTextsList = false,
  onSent,
  onCancel,
}: {
  /** Who may be chosen, each with what reaches them where the place knows it. */
  people: readonly ComposerPerson[];
  /** The channels offered, the first open; a reply to her text is a text whatever this says. */
  channels?: readonly ComposerChannel[];
  answering?: AnsweredText | null;
  quote?: boolean;
  onTextsList?: boolean;
  /** Called with what happened and on which channel, once the door has written it. */
  onSent: (sentence: string, channel: ComposerChannel) => void;
  onCancel: () => void;
}) {
  const offered: readonly ComposerChannel[] =
    answering !== null || channels.length === 0 ? ['sms'] : channels;
  const [channel, setChannel] = useState<ComposerChannel>(offered[0]!);
  const switcher =
    offered.length > 1 ? (
      <div role="group" aria-label="Send it as" className="mt-2 flex flex-wrap gap-2">
        {offered.map((one) => (
          <button
            key={one}
            type="button"
            aria-pressed={one === channel}
            onClick={() => setChannel(one)}
            className={`rounded-lg border px-3 text-sm font-medium ${TARGET_SIZE} ${
              one === channel
                ? 'border-navy bg-navy text-cream'
                : 'border-navy/25 bg-cream text-navy'
            }`}
          >
            {CHANNEL_LABELS[one]}
          </button>
        ))}
      </div>
    ) : null;
  const reachable = people.filter(reachableBy(channel));
  return channel === 'email' ? (
    <EmailForm
      key="email"
      people={reachable}
      switcher={switcher}
      onSent={(sentence) => onSent(sentence, 'email')}
      onCancel={onCancel}
    />
  ) : (
    <TextForm
      key="sms"
      people={answering === null ? reachable : people}
      answering={answering}
      quote={quote}
      onTextsList={onTextsList}
      switcher={switcher}
      onSent={(sentence) => onSent(sentence, 'sms')}
      onCancel={onCancel}
    />
  );
}

/**
 * THE TEXT ALONE (ops #32), as a household's thread and the Texts list open it: the composer offering
 * the one channel.
 */
export function TextComposer(props: {
  people: readonly ComposerPerson[];
  answering?: AnsweredText | null;
  quote?: boolean;
  onTextsList?: boolean;
  onSent: (sentence: string) => void;
  onCancel: () => void;
}) {
  const { onSent, ...rest } = props;
  return <Composer {...rest} channels={['sms']} onSent={(sentence) => onSent(sentence)} />;
}

function TextForm({
  people,
  answering = null,
  quote = true,
  onTextsList = false,
  switcher = null,
  onSent,
  onCancel,
}: {
  /** Who may be chosen: one when answering, her people with a number on a household's thread. */
  people: readonly ComposerPerson[];
  /** The text of hers this answers, or null for a text that answers none. */
  answering?: AnsweredText | null;
  /** Whether her words are quoted above the box: not where they are already right above it. */
  quote?: boolean;
  /**
   * Whether it is open on the Texts list, where a stop waiting for a decision is in the band at the
   * top and a text the phone system failed is not, rather than on her household's page.
   */
  onTextsList?: boolean;
  /** The channel switch, where the place offers more than a text. */
  switcher?: ReactNode;
  /** Called with what happened, once the door has written the text. */
  onSent: (sentence: string) => void;
  onCancel: () => void;
}) {
  const base = answering?.id ?? 'new';
  const field = (name: string) => `compose-${name}-${base}`;
  const [contactId, setContactId] = useState(answering?.contactId ?? people[0]?.id ?? '');
  const [context, setContext] = useState<ComposeContext | null>(null);
  const [unread, setUnread] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [kind, setKind] = useState<ComposerKind>('reply');
  const [visitId, setVisitId] = useState('');
  // Null until her read lands, which is when the signature goes in; never put back over typing.
  const [body, setBody] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [refused, setRefused] = useState<Refused | null>(null);
  // What the client reads of the visit's report, once a finished clean names one (ops #98 phase 4).
  const [finished, readFinishedAgain] = useFinishedWords(contactId, kind, visitId);

  useEffect(() => {
    if (contactId === '') return;
    let live = true;
    setContext(null);
    setUnread(null);
    getComposeContext(contactId)
      .then((answer) => {
        if (!live) return;
        setContext(answer);
        setBody((typed) => typed ?? answer.signature);
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        setUnread(
          thrown instanceof ApiError
            ? thrown
            : new ApiError(0, 'error', 'Where their texts stand did not load.'),
        );
      });
    return () => {
      live = false;
    };
  }, [contactId, attempt]);

  /** Her read again, quietly, after the register or the switch refused what was sent. */
  function readHerAgain() {
    getComposeContext(contactId)
      .then((answer) =>
        setContext((current) =>
          current !== null && current.contact.id === answer.contact.id ? answer : current,
        ),
      )
      .catch(() => {
        // The refusal already says why; a read that fails here leaves what was read before.
      });
  }

  function choosePerson(id: string) {
    setContactId(id);
    setVisitId('');
    setRefused(null);
  }

  const typed = body ?? '';
  const words = typed.trim();
  const optOutAdded = !words.toLowerCase().includes(OPT_OUT_WORDS);
  const parts = textParts(optOutAdded ? `${words} ${OPT_OUT_LINE}` : words);
  const findings = checkWords(words);
  // The code warning names no phrase: the phrase would be the code.
  const warnings = [
    ...(looksLikeACode(words) ? [{ key: 'code', line: CODE_WARNING }] : []),
    ...warningsIn(words).map((warning) => ({
      key: `${warning.rule}-${warning.phrase}`,
      line: `"${warning.phrase}": ${warning.say}`,
    })),
  ];
  const why =
    context === null
      ? null
      : whyNot(
          context,
          findings.length,
          parts,
          words,
          onTextsList,
          kind,
          finishedWhyNot(kind, visitId, context.visits, finished),
        );
  const name =
    context?.contact.name ??
    people.find((person) => person.id === contactId)?.displayName ??
    'them';

  async function send() {
    if (context === null || why !== null) return;
    setBusy(true);
    setRefused(null);
    try {
      const result = await composeText({
        contactId: context.contact.id,
        kind,
        body: words,
        ...(answering === null ? {} : { inReplyTo: answering.id }),
        ...(visitId === '' ? {} : { visitId }),
      });
      onSent(sentWords(result));
    } catch (thrown) {
      if (thrown instanceof ApiError) {
        setRefused({
          code: thrown.code,
          message: thrown.message,
          findings: findingsOf(thrown.body),
        });
        if (READ_AGAIN_ON.has(thrown.code)) readHerAgain();
      } else {
        setRefused({ code: 'error', message: 'That was not sent. Try it again.', findings: [] });
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-composer className="mt-3 min-w-0 rounded-lg border border-navy/15 bg-parchment p-3">
      <h4 className="text-sm font-medium">{answering ? 'Reply to their text' : 'Write a text'}</h4>
      {switcher}
      {answering && quote ? (
        <blockquote className="mt-2 border-l-4 border-navy/20 pl-3 text-sm whitespace-pre-wrap text-navy/80 wrap-anywhere">
          {answering.body ?? 'No words to show.'}
        </blockquote>
      ) : null}

      <div className="mt-3">
        {answering === null && people.length > 1 ? (
          <SelectField
            id={field('person')}
            label="To"
            value={contactId}
            onChange={choosePerson}
            options={people.map((person) => person.id)}
            toLabel={(id) => people.find((person) => person.id === id)?.displayName ?? id}
          />
        ) : (
          <p className="mb-3 text-sm wrap-anywhere">To {name}</p>
        )}
      </div>

      {unread ? (
        <div className="mb-3">
          {unread.status === 422 ? (
            <ErrorNotice title="Not texted from here" message={unread.message} />
          ) : (
            <ErrorNotice
              title="Where their texts stand did not load"
              message={unread.message}
              onRetry={() => setAttempt((n) => n + 1)}
            />
          )}
        </div>
      ) : context === null ? (
        <Busy what="where their texts stand" />
      ) : (
        <>
          <SelectField
            id={field('kind')}
            label="What kind of text"
            value={kind}
            onChange={setKind}
            options={COMPOSER_KINDS}
            toLabel={(value) => COMPOSER_KIND_LABELS[value]}
          />
          {context.visits.length > 0 ? (
            <SelectField
              id={field('visit')}
              label="About a visit"
              value={visitId}
              onChange={setVisitId}
              options={context.visits.map((visit) => visit.id)}
              toLabel={visitWords(context.visits)}
              placeholder="No visit in particular"
            />
          ) : null}
          <WhatTheClientReads read={finished} channel="sms" onRetry={readFinishedAgain} />
          <TextAreaField
            id={field('body')}
            label="What they read"
            value={typed}
            onChange={setBody}
            rows={5}
          />

          <div aria-live="polite">
            {findings.length > 0 ? (
              <ul aria-label="Must change before it can go" className="mb-3 flex flex-col gap-2">
                {findings.map((finding) => (
                  <li
                    key={`${finding.rule}-${finding.phrase}`}
                    className={`text-sm wrap-anywhere ${CORAL_LINE} ${MEASURE}`}
                  >
                    {`"${finding.phrase}": ${finding.say}`}
                  </li>
                ))}
              </ul>
            ) : null}
            {warnings.length > 0 ? (
              <ul aria-label="Read it once more" className="mb-3 flex flex-col gap-2">
                {warnings.map((warning) => (
                  <li
                    key={warning.key}
                    className={`text-sm wrap-anywhere ${AMBER_LINE} ${MEASURE}`}
                  >
                    {warning.line}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>

          <ul aria-label="Before it goes" className={`mb-3 flex flex-col gap-2 text-sm ${MEASURE}`}>
            <li className={parts > MAX_TEXT_PARTS ? CORAL_LINE : ''}>
              {partsWords(parts, optOutAdded)}
            </li>
            {context.hours.open ? null : (
              <li>
                Texts go {TEXTING_HOURS_WORDS} in Florida, and it is outside them now: the phone
                system holds this one until{' '}
                {shortInstant(context.hours.opensAt) ?? 'they open again'}.
              </li>
            )}
            <li>
              {context.counted.inLast24h} of {context.counted.cap} texts to this number in the last
              24 hours, not counting replies to their texts.{' '}
              {answering
                ? 'This one answers their text, so it is not counted.'
                : context.counted.inLast24h < context.counted.cap
                  ? 'This one counts, since it answers no text of theirs.'
                  : null}
            </li>
            {answering === null && context.counted.inLast24h >= context.counted.cap ? (
              <li className={CORAL_LINE}>
                The phone system will refuse this one as a fourth in a day, unless it answers one of
                their texts.
              </li>
            ) : null}
            {context.confirmationNeeded ? (
              <li>Their first text: the opt-in confirmation goes ahead of it.</li>
            ) : null}
          </ul>

          {refused ? (
            <div className="mb-3">
              <ErrorNotice title="Not sent" message={refusedWords(refused, context, onTextsList)} />
              {refused.findings.length > 0 ? (
                <ul aria-label="What the check found" className="mt-2 flex flex-col gap-2">
                  {refused.findings.map((finding) => (
                    <li
                      key={`${finding.rule}-${finding.phrase}`}
                      className={`text-sm wrap-anywhere ${CORAL_LINE} ${MEASURE}`}
                    >
                      {`"${finding.phrase}": ${finding.say}`}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}

          {why ? (
            <p id={field('why')} className={`mb-2 text-sm wrap-anywhere ${MEASURE}`}>
              {why}
            </p>
          ) : null}
        </>
      )}

      <div className="flex flex-wrap gap-2">
        {context !== null && unread === null ? (
          <Button
            onClick={() => void send()}
            disabled={busy || why !== null}
            describedBy={why === null ? undefined : field('why')}
          >
            {busy ? 'Sending' : 'Send'}
          </Button>
        ) : null}
        <Button tone="quiet" onClick={onCancel} disabled={busy}>
          Not now
        </Button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * The email (ops #84 phase 5).
 * ------------------------------------------------------------------ */

/**
 * What the office reads while client email is switched off: the door writes nothing, so the email
 * cannot be sent and nothing of it is saved, and nothing goes later on its own.
 */
const EMAIL_HELD_WHILE_OFF =
  'Client email is switched off in ops, so this email cannot be sent, and nothing is saved. An owner turns client email on in Settings.';

/** What the office reads while client email goes to test addresses only, and theirs is not one. */
const EMAIL_HELD_NOT_LISTED =
  'Ops emails test addresses only while client email is being tried, and theirs is not one, so this email cannot be sent, and nothing is saved. An owner lists the test addresses in Settings.';

/** Why the switch for client email refuses an email to them, in words that say nothing is saved. */
export function emailHeldWords(refusal: string): string {
  if (refusal === EMAIL_OFF) return EMAIL_HELD_WHILE_OFF;
  if (refusal === NOT_A_TEST_ADDRESS) return EMAIL_HELD_NOT_LISTED;
  return `${plainWords(refusal)} This email cannot be sent, and nothing is saved.`;
}

/** Why the register will not have them emailed, and the way on where a mark does not forbid one. */
function emailRegisterWords(emailing: EmailComposeContext['emailing']): string {
  const why =
    emailing.refusal === null
      ? 'There is no yes to email from them on record, so nothing can be emailed to them.'
      : plainWords(emailing.refusal);
  return emailing.markId !== null ? why : `${why} Ring them, or text them.`;
}

/** Why Send is disabled on an email, before anything is sent, or null when it may be pressed. */
function whyNotEmail(
  context: EmailComposeContext,
  findings: number,
  words: string,
  kind: ComposerKind = 'reply',
  finished: string | null = null,
): string | null {
  if (context.contact.email === null) {
    return 'They have no email address on file. Add it on their record, or ring or text them.';
  }
  if (!context.emailing.allowed) return emailRegisterWords(context.emailing);
  if (context.switch.refusal !== null) return emailHeldWords(context.switch.refusal);
  // Her choice or the office's hold on this kind (ops #76 phase 2), as the door asks it.
  const chosen = choiceLineOf(context.choices?.[kind]);
  if (chosen !== null) return chosen;
  // Message 11 waits for what the client reads of its visit's report (ops #98 phase 4).
  if (finished !== null) return finished;
  if (findings > 0) return 'Change the words marked in coral, then send.';
  if (words === '') return 'Write what they should read.';
  return null;
}

/** A refusal of the door in the office's words: the register's and the switch's said plainly. */
function emailRefusedWords(refused: Refused): string {
  if (refused.code === 'emails_not_allowed') return plainWords(refused.message);
  if (refused.code === 'email_off') return emailHeldWords(refused.message);
  return refused.message;
}

/** The refusals after which their read is asked again, so the reason beside Send is the one now. */
const EMAIL_READ_AGAIN_ON: ReadonlySet<string> = new Set(['emails_not_allowed', 'email_off']);

/** What the door wrote, as the office reads it once the box has closed. */
export function emailSentWords(result: ComposedText): string {
  return [
    'Approved. Home sends it from home@, and the Emails card says when it is delivered.',
    ...(result.waitsForHerDoor
      ? ['It waits until home holds their door, since it carries their page there.']
      : []),
  ].join(' ');
}

/** The subject home sends it under, fixed by its kind and the day of the clean it names. */
export function subjectOf(kind: ComposerKind, day: string | null): string {
  switch (kind) {
    case 'booking_confirmation':
    case 'visit_finished':
      return officeEmailSubject({ kind, day });
    case 'reply':
    case 'access_problem':
      return officeEmailSubject({ kind });
  }
}

/** Words that carry something like a link, which goes only to a page at home. */
const LINK_LIKE = /\bhttp|\bwww\./i;

function EmailForm({
  people,
  switcher = null,
  onSent,
  onCancel,
}: {
  /** Who may be chosen: the household's people with an address. */
  people: readonly ComposerPerson[];
  switcher?: ReactNode;
  onSent: (sentence: string) => void;
  onCancel: () => void;
}) {
  const field = (name: string) => `compose-email-${name}`;
  const [contactId, setContactId] = useState(people[0]?.id ?? '');
  const [context, setContext] = useState<EmailComposeContext | null>(null);
  const [unread, setUnread] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [kind, setKind] = useState<ComposerKind>('reply');
  const [visitId, setVisitId] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [refused, setRefused] = useState<Refused | null>(null);
  // What the client reads of the visit's report, once a finished clean names one (ops #98 phase 4).
  const [finished, readFinishedAgain] = useFinishedWords(contactId, kind, visitId);

  useEffect(() => {
    if (contactId === '') return;
    let live = true;
    setContext(null);
    setUnread(null);
    getEmailComposeContext(contactId)
      .then((answer) => {
        if (live) setContext(answer);
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        setUnread(
          thrown instanceof ApiError
            ? thrown
            : new ApiError(0, 'error', 'Where their email stands did not load.'),
        );
      });
    return () => {
      live = false;
    };
  }, [contactId, attempt]);

  function readThemAgain() {
    getEmailComposeContext(contactId)
      .then((answer) =>
        setContext((current) =>
          current !== null && current.contact.id === answer.contact.id ? answer : current,
        ),
      )
      .catch(() => {
        // The refusal already says why; a read that fails here leaves what was read before.
      });
  }

  function choosePerson(id: string) {
    setContactId(id);
    setVisitId('');
    setRefused(null);
  }

  const words = body.trim();
  const findings = checkWords(words);
  const warnings = [
    ...(looksLikeACode(words) ? [{ key: 'code', line: CODE_WARNING }] : []),
    ...(LINK_LIKE.test(words)
      ? [
          {
            key: 'link',
            line: 'A link goes only to a page at home. Any other link is refused when you send, so take it out.',
          },
        ]
      : []),
    ...warningsIn(words).map((warning) => ({
      key: `${warning.rule}-${warning.phrase}`,
      line: `"${warning.phrase}": ${warning.say}`,
    })),
  ];
  const day =
    visitId === ''
      ? null
      : (context?.visits.find((visit) => visit.id === visitId)?.scheduledDate ?? null);
  const subject = subjectOf(kind, day);
  const why =
    context === null
      ? null
      : whyNotEmail(
          context,
          findings.length,
          words,
          kind,
          finishedWhyNot(kind, visitId, context.visits, finished),
        );
  const name =
    context?.contact.name ??
    people.find((person) => person.id === contactId)?.displayName ??
    'them';

  async function send() {
    if (context === null || why !== null) return;
    setBusy(true);
    setRefused(null);
    try {
      const result = await composeText({
        contactId: context.contact.id,
        kind,
        channel: 'email',
        body: words,
        ...(visitId === '' ? {} : { visitId }),
      });
      onSent(emailSentWords(result));
    } catch (thrown) {
      if (thrown instanceof ApiError) {
        setRefused({
          code: thrown.code,
          message: thrown.message,
          findings: findingsOf(thrown.body),
        });
        if (EMAIL_READ_AGAIN_ON.has(thrown.code)) readThemAgain();
      } else {
        setRefused({ code: 'error', message: 'That was not sent. Try it again.', findings: [] });
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      data-composer
      data-channel="email"
      className="mt-3 min-w-0 rounded-lg border border-navy/15 bg-parchment p-3"
    >
      <h4 className="text-sm font-medium">Write an email</h4>
      {switcher}

      <div className="mt-3">
        {people.length === 0 ? (
          <p className={`mb-3 text-sm wrap-anywhere ${MEASURE}`}>
            Nobody on this household has an email address on file. Add one on their record first.
          </p>
        ) : people.length > 1 ? (
          <SelectField
            id={field('person')}
            label="To"
            value={contactId}
            onChange={choosePerson}
            options={people.map((person) => person.id)}
            toLabel={(id) => people.find((person) => person.id === id)?.displayName ?? id}
          />
        ) : (
          <p className="mb-3 text-sm wrap-anywhere">To {name}</p>
        )}
      </div>

      {people.length === 0 ? null : unread ? (
        <div className="mb-3">
          {unread.status === 422 ? (
            <ErrorNotice title="Not emailed from here" message={unread.message} />
          ) : (
            <ErrorNotice
              title="Where their email stands did not load"
              message={unread.message}
              onRetry={() => setAttempt((n) => n + 1)}
            />
          )}
        </div>
      ) : context === null ? (
        <Busy what="where their email stands" />
      ) : (
        <>
          {context.contact.email !== null ? (
            <p className="mb-3 text-sm wrap-anywhere">{context.contact.email}</p>
          ) : null}
          <SelectField
            id={field('kind')}
            label="What kind of email"
            value={kind}
            onChange={setKind}
            options={COMPOSER_KINDS}
            toLabel={(value) => COMPOSER_KIND_LABELS[value]}
          />
          {context.visits.length > 0 ? (
            <SelectField
              id={field('visit')}
              label="About a visit"
              value={visitId}
              onChange={setVisitId}
              options={context.visits.map((visit) => visit.id)}
              toLabel={visitWords(context.visits)}
              placeholder="No visit in particular"
            />
          ) : null}
          <p className="mb-3 text-sm wrap-anywhere">
            <span className="text-navy/70">Subject, fixed by the kind: </span>
            <span data-subject>{subject}</span>
          </p>
          <WhatTheClientReads
            read={finished}
            channel="email"
            onRetry={readFinishedAgain}
            onUse={setBody}
          />
          <TextAreaField
            id={field('body')}
            label="What they read"
            value={body}
            onChange={setBody}
            rows={8}
          />
          <div className={`mb-3 text-sm ${MEASURE}`}>
            <p className="text-navy/70">After the words, every email ends with:</p>
            <p data-footer className="mt-1 whitespace-pre-wrap text-navy/80">
              {FOOTER}
            </p>
          </div>

          <div aria-live="polite">
            {findings.length > 0 ? (
              <ul aria-label="Must change before it can go" className="mb-3 flex flex-col gap-2">
                {findings.map((finding) => (
                  <li
                    key={`${finding.rule}-${finding.phrase}`}
                    className={`text-sm wrap-anywhere ${CORAL_LINE} ${MEASURE}`}
                  >
                    {`"${finding.phrase}": ${finding.say}`}
                  </li>
                ))}
              </ul>
            ) : null}
            {warnings.length > 0 ? (
              <ul aria-label="Read it once more" className="mb-3 flex flex-col gap-2">
                {warnings.map((warning) => (
                  <li
                    key={warning.key}
                    className={`text-sm wrap-anywhere ${AMBER_LINE} ${MEASURE}`}
                  >
                    {warning.line}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>

          <ul aria-label="Before it goes" className={`mb-3 flex flex-col gap-2 text-sm ${MEASURE}`}>
            <li>
              Home delivers it from home@, under the subject above
              {context.author ? `, naming ${context.author} as who wrote it` : ''}. It goes within a
              day, or not at all.
            </li>
            {context.switch.refusal !== null ? (
              <li className={CORAL_LINE}>{emailHeldWords(context.switch.refusal)}</li>
            ) : null}
          </ul>

          {refused ? (
            <div className="mb-3">
              <ErrorNotice title="Not sent" message={emailRefusedWords(refused)} />
              {refused.findings.length > 0 ? (
                <ul aria-label="What the check found" className="mt-2 flex flex-col gap-2">
                  {refused.findings.map((finding) => (
                    <li
                      key={`${finding.rule}-${finding.phrase}`}
                      className={`text-sm wrap-anywhere ${CORAL_LINE} ${MEASURE}`}
                    >
                      {`"${finding.phrase}": ${finding.say}`}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}

          {why ? (
            <p id={field('why')} className={`mb-2 text-sm wrap-anywhere ${MEASURE}`}>
              {why}
            </p>
          ) : null}
        </>
      )}

      <div className="flex flex-wrap gap-2">
        {context !== null && unread === null && people.length > 0 ? (
          <Button
            onClick={() => void send()}
            disabled={busy || why !== null}
            describedBy={why === null ? undefined : field('why')}
          >
            {busy ? 'Sending' : 'Send'}
          </Button>
        ) : null}
        <Button tone="quiet" onClick={onCancel} disabled={busy}>
          Not now
        </Button>
      </div>
    </div>
  );
}
